import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { forumPath } from '@/features/common/forumPath';
import { useStatusLine } from '@/features/common/useStatusLine';
import { communityApi } from '@/features/publicsite/community/api/communityApi';
import type { ForumDetail } from '@/features/publicsite/types/community/ForumDetail';
import type { ForumTopic } from '@/features/publicsite/types/community/ForumTopic';
import type { LoadState } from '@/features/publicsite/types/community/LoadState';
import { forumLoadFailure } from '@/features/publicsite/community/forumLoadFailure';
import type { SpaceMember } from '@/features/publicsite/types/community/SpaceMember';

/**
 * Everything a forum page reads and writes: the forum itself, its topics, and — for a private
 * space — its roster. The page composes it and renders; nothing here touches the DOM.
 */
export function useForum(id: string) {
  const navigate = useNavigate();
  const [forum, setForum] = useState<ForumDetail | null>(null);
  const [topics, setTopics] = useState<ForumTopic[] | null>(null);
  const [members, setMembers] = useState<SpaceMember[] | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const { status, statusKind, succeed, fail } = useStatusLine();

  // Set right before a canonicalizing redirect (below) and consumed by the next run of this
  // effect — same guard EventDetailPage uses, and for the same reason: without it, navigate()
  // changing the :id param (which this effect depends on) re-triggered the same fetch for data
  // just loaded.
  const skipNextFetchRef = useRef(false);

  const refreshTopics = () => communityApi.topics(id).then(setTopics);

  useEffect(() => {
    let live = true;
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    setState('loading');
    communityApi
      .forum(id)
      .then((f) => {
        if (!live) return;
        setForum(f);
        setState('ok');
        // A visitor who arrived on a bare id — an old link, a bookmark, a hand-typed URL — gets
        // the readable address once the slug is known. `replace` so the back button still goes
        // back to the community page rather than to the same page under its other name.
        const canonical = forumPath(f);
        if (canonical !== `/comunitate/forumuri/${id}`) {
          skipNextFetchRef.current = true;
          navigate(canonical, { replace: true });
        }
      })
      .catch((e) => {
        if (!live) return;
        setState(forumLoadFailure(e));
      });
    // Not chained onto the forum fetch above: the redirect it can trigger changes :id, which
    // re-runs this effect and flips `live` to false in the outgoing instance's cleanup — a topics
    // fetch chained after the redirect would then lose its own race and never call setTopics.
    // Firing it in parallel instead means it settles (or the whole effect re-runs) before that
    // matters, the same shape EventDetailPage uses for its own secondary (feedback) fetch.
    communityApi
      .topics(id)
      .then((t) => {
        if (live) setTopics(t);
      })
      .catch(() => {
        /* a forbidden or missing forum's own fetch above already reports that; nothing more to
           say here if its topics also fail to load */
      });
    return () => {
      live = false;
    };
  }, [id, navigate]);

  const reloadMembers = () =>
    communityApi
      .members(id)
      .then(setMembers)
      .catch(() => setMembers([]));

  useEffect(() => {
    if (forum?.visibility !== 'PRIVATE' || state !== 'ok') return;
    void reloadMembers();
    // Once per space: reloadMembers is a fresh closure every render over the same `id`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, forum?.visibility, state]);

  return {
    forum,
    topics,
    members,
    state,
    status,
    statusKind,
    succeed,
    fail,
    reloadMembers,
    createTopic: async (title: string) => {
      if (!title.trim()) return;
      await communityApi.createTopic(id, title.trim());
      await refreshTopics();
    },
  };
}
